import React from 'react'
import PropTypes from 'prop-types'
import {ChevronDown, Close} from '../Icons/index.jsx'
import {isString} from '../Services/helpers.jsx'
import {
  isElementChildOf,
  addPreventScrollUnderModal,
  removePreventScrollUnderModal,
} from '../Services/domBridge.jsx'
import Dropdown from './Dropdown.jsx'

export default class SelectField extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    mostPopularOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    activeOption: PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
    ]),
    activeOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    onSelect: PropTypes.func,
    onChange: PropTypes.func,
    isValid: PropTypes.bool,
    showValidation: PropTypes.bool,
    showSearchBar: PropTypes.bool,
    isMultipleSelect: PropTypes.bool,
    onToggleOption: PropTypes.func,
    optionsSelectedCopySingular: PropTypes.func,
    optionsSelectedCopyPlural: PropTypes.func,
    resetSelectedOptions: PropTypes.func,
    showMultipleSelectLabel: PropTypes.string,
    dataTestId: PropTypes.string,
    invalidMessage: PropTypes.string,
  }

  static defaultProps = {
    onSelect: Function.prototype,
    isMultipleSelect: false,
    optionsSelectedCopySingular: Function.prototype,
    optionsSelectedCopyPlural: Function.prototype,
    onToggleOption: Function.prototype,
    resetSelectedOptions: Function.prototype,
    showMultipleSelectLabel: '',
  }

  constructor(props) {
    super(props)
    this.textInput = React.createRef()
    this.listRef = React.createRef()

    this.state = {
      value: props.activeOption ? props.activeOption.id : '',
      isDropdownVisible: false,
      isMultipleSelectMode: false,
    }
  }

  componentDidUpdate = (previousProps) => {
    const {activeOption} = this.props
    if (previousProps.activeOption !== activeOption)
      this.setState({value: activeOption.id})
  }

  getActiveOptionName = () => {
    const {isMultipleSelect, activeOption, activeOptions} = this.props
    if (isMultipleSelect && activeOptions)
      return activeOptions.length
        ? activeOptions.map((option) => option.name).join(', ')
        : undefined
    else return activeOption ? activeOption.name : undefined
  }

  getInputClassName = () => {
    const {isValid, activeOption, showValidation, activeOptions} = this.props
    const {isDropdownVisible} = this.state
    const inputPlaceholderClassName =
      !activeOption && activeOptions && activeOptions.length === 0
        ? 'select--is-placeholder'
        : ''
    const inputIsFocusedClassName = isDropdownVisible
      ? 'select--is-focused'
      : ''
    const inputIsInvalidClassName =
      showValidation && !isValid ? 'select--is-invalid' : ''
    return `select input--is-select ${inputPlaceholderClassName} ${inputIsFocusedClassName} ${inputIsInvalidClassName}`
  }

  onShowMultipleSelectClick = (e) => {
    const {isDropdownVisible} = this.state
    this.setState(
      {
        isMultipleSelectMode: true,
      },
      addPreventScrollUnderModal,
    )
    if (isDropdownVisible) {
      if (e) {
        e.stopPropagation()
        e.nativeEvent.stopImmediatePropagation()
      }
    }
  }

  stopMultipleSelectClick = () => {
    this.setState(
      {
        isMultipleSelectMode: false,
      },
      removePreventScrollUnderModal,
    )
  }

  updateDropdownMaxHeight = () => {
    const {isMultipleSelectMode} = this.state
    if (!isMultipleSelectMode && this.listRef.current) {
      const listRefNode = this.listRef.current
      const listRefTopPosition = listRefNode.getBoundingClientRect().top
      const screenHeight = window.innerHeight
      const maxHeight = screenHeight - listRefTopPosition - 16
      listRefNode.style.maxHeight = `${maxHeight > 128 ? maxHeight : 128}px`
    }
  }

  showDropDown = () => {
    this.setState({isDropdownVisible: true}, this.updateDropdownMaxHeight)
  }

  hideDropDown = () => {
    document.removeEventListener('mousedown', this.checkIfShouldHideDropDown)
    document.removeEventListener('keydown', this.checkIfShouldHideDropDown)
    removePreventScrollUnderModal()
    this.setState({
      isDropdownVisible: false,
      isMultipleSelectMode: false,
    })
  }

  toggleFocus = () => {
    const {isDropdownVisible} = this.state
    if (isDropdownVisible) {
      setTimeout(this.blurInput, 0)
    } else {
      setTimeout(this.focusInput, 0)
    }
  }

  focusInput = () => {
    this.textInput.current.focus()
  }

  blurInput = () => {
    this.textInput.current.blur()
  }

  checkIfShouldHideDropDown = (event) => {
    const targetClassName = isString(event.target.className)
      ? event.target.className
      : ''
    const isTabPressed = event.keyCode === 9
    const isEscPressed = event.keyCode === 27

    //todo: rewrite this if statement
    if (
      targetClassName.indexOf('dropdown__search-bar-input') > -1 &&
      !isTabPressed &&
      !isEscPressed
    ) {
      // check mouse click on dropdown search input
      /* do nothing */
    } else if (
      targetClassName.indexOf('dropdown__list-callback-action') > -1 &&
      !isTabPressed &&
      !isEscPressed
    ) {
      // when click on callback action link
      /* do nothing */
    } else if (isElementChildOf(event.target, '.dropdown') && !isEscPressed) {
      // when multiple selection check mouse click on dropdown div
      /* do nothing */
    } else {
      this.focusInput()
      setTimeout(() => {
        this.hideDropDown()
        document.removeEventListener(
          'mousedown',
          this.checkIfShouldHideDropDown,
        )
        document.removeEventListener('keydown', this.checkIfShouldHideDropDown)
        this.stopMultipleSelectClick()
      }, 0)
    }
  }

  onFocus = () => {
    const {activeOptions, showSearchBar} = this.props
    if (activeOptions && activeOptions.length > 1) {
      this.onShowMultipleSelectClick()
    }
    this.showDropDown()
    if (showSearchBar) {
      document.addEventListener('mousedown', this.checkIfShouldHideDropDown)
      document.addEventListener('keydown', this.checkIfShouldHideDropDown)
    }
  }

  onBlur = () => {
    const {showSearchBar} = this.props
    if (!showSearchBar) {
      this.hideDropDown()
    }
  }

  onSelect = (option) => {
    const {onChange, activeOptions, onSelect} = this.props

    onChange && onChange(option)

    if (option) {
      onSelect(option)
    } else if (activeOptions && activeOptions.length) {
      onSelect(activeOptions)
    }

    this.hideDropDown()

    if (option) this.setState({value: option.id})
  }

  render = () => {
    const {
      name,
      showSearchBar,
      activeOption,
      activeOptions,
      options,
      onToggleOption,
      mostPopularOptions,
      placeholder,
      label,
      optionsSelectedCopySingular,
      optionsSelectedCopyPlural,
      resetSelectedOptions,
      isMultipleSelect,
      showMultipleSelectLabel,
      className,
      dataTestId,
      showValidation,
      isValid,
      invalidMessage,
    } = this.props
    const {value = '', isDropdownVisible, isMultipleSelectMode} = this.state
    const activeOptionName = this.getActiveOptionName()
    const isMultipleSelectLabelVisible =
      isMultipleSelect && !isMultipleSelectMode
    const inputClassName = this.getInputClassName()

    const inputValue =
      (activeOptions && activeOptions.map((v) => v.id).join(',')) || value

    return (
      <div
        className={`select-with-label__wrapper ${className ? className : ''}`}
      >
        {/* tofix: storage js is sooo messy, we're going to have plain inputs in dom to have a safe way to get input value from DOM at any moment */}
        <input type={'hidden'} name={`${name}-hidden`} value={inputValue} />
        <label onMouseDown={this.toggleFocus}>
          {label}
          {isMultipleSelect &&
          showMultipleSelectLabel &&
          activeOptions &&
          activeOptions.length < 2 ? (
            <span onMouseDown={this.onShowMultipleSelectClick}>
              {showMultipleSelectLabel}
            </span>
          ) : (
            ''
          )}
        </label>
        <div className={'select-with-icon__wrapper'}>
          <span
            className={inputClassName}
            onMouseDown={this.toggleFocus}
            data-testid={dataTestId}
          >
            {activeOptionName || placeholder}
          </span>
          <input
            name={name}
            readOnly={true}
            ref={this.textInput}
            type={'text'}
            className={'input--invisible'}
            placeholder={placeholder}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            value={value}
          />
          <ChevronDown
            className={'select-with-icon__right-icon'}
            onMouseDown={this.toggleFocus}
          />
        </div>
        {showValidation && !isValid && invalidMessage && (
          <span className={'input__invalid-message'}>{invalidMessage}</span>
        )}
        {isDropdownVisible && (
          <div
            className={`select__dropdown-wrapper ${
              isMultipleSelectLabelVisible ? '' : ''
            } ${
              isMultipleSelectMode
                ? 'select__dropdown-wrapper--is-multiselect'
                : ''
            }`}
          >
            <div className={'select__mobile-dropdown-header'}>
              <label className={'select__mobile-dropdown-label'}>{label}</label>
              <Close onMouseDown={this.hideDropDown} />
            </div>
            {isMultipleSelectMode ? (
              <Dropdown
                className={'select__dropdown'}
                showSearchBar={showSearchBar}
                listRef={this.listRef}
                activeOption={activeOption}
                activeOptions={activeOptions}
                options={options}
                onSelect={this.onSelect}
                onToggleOption={onToggleOption}
                isMultipleSelect={true}
                mostPopularOptions={mostPopularOptions}
                optionsSelectedCopySingular={optionsSelectedCopySingular}
                optionsSelectedCopyPlural={optionsSelectedCopyPlural}
                resetSelectedOptions={resetSelectedOptions}
                onClose={this.hideDropDown}
              />
            ) : (
              <Dropdown
                className={'select__dropdown'}
                showSearchBar={showSearchBar}
                listRef={this.listRef}
                activeOption={activeOption}
                activeOptions={activeOptions}
                options={options}
                onSelect={this.onSelect}
                onToggleOption={onToggleOption}
                isMultipleSelect={false}
                optionsSelectedCopySingular={optionsSelectedCopySingular}
                optionsSelectedCopyPlural={optionsSelectedCopyPlural}
                resetSelectedOptions={resetSelectedOptions}
                callbackActionLabel={
                  isMultipleSelect && showMultipleSelectLabel
                    ? showMultipleSelectLabel
                    : undefined
                }
                callbackActionOnClick={
                  isMultipleSelect ? this.onShowMultipleSelectClick : undefined
                }
              />
            )}
          </div>
        )}
      </div>
    )
  }
}
